export const REMIX_MENU_EXPERIMENT = 'cc-ce-maui-remix-menu-multivariant' as const;
export const REMIX_MENU_AA_EXPERIMENT = 'cc-ce-maui-remix-menu-multivariant-aa' as const;
export const REMIX_MENU_COHORT_PARAM = 'cohort' as const;
export const REMIX_MENU_AA_VARIANT_PARAM = 'variant' as const;
export const REMIX_MENU_CONTROL_COHORT = 'control' as const;
export const REMIX_MENU_INLINE_DROPDOWN_COHORT = 'variant1' as const;

/** Node types we never show Remix for. */
export const REMIX_DENY_LIST_V1: readonly string[] = [
	'mediaSingle',
	'mediaGroup',
	'media',
	'image',
	'extension',
	'bodiedExtension',
	'multiBodiedExtension',
	'embedCard',
	'blockCard',
	'inlineCard',
	// Source blocks
	'bodiedSyncBlock',
	// Reference blocks
	'syncBlock',
	'expand',
	'nestedExpand',
] as const;

const DENY_SET = new Set<string>(REMIX_DENY_LIST_V1);

/** True if this node type is in the remix deny list (restricted). */
export const isRemixRestrictedNodeType = (nodeType: string): boolean => DENY_SET.has(nodeType);

/**
 * `cc_maui_polish_changes_batch_7`: enables Remix for `expand` / `nestedExpand`.
 *
 * Expands stay in `REMIX_DENY_LIST_V1` so `isRemixRestrictedNodeType` and every product that
 * relies on the shared default keep their current behaviour. Only callers that opt in via
 * `isRemixRestrictedNodeTypeWithExpands` see expands as supported.
 *
 * Like `REMIX_MENU_EXPERIMENT`, the key is declared here but read by each product with its own
 * experiment runtime — Confluence product code uses `@confluence/feature-experiments` while
 * platform code uses `@atlaskit/platform-feature-experiments`, and only the product's own runtime
 * is controllable from that product's tests.
 */
export const REMIX_EXPANDS_EXPERIMENT = 'cc_maui_polish_changes_batch_7' as const;

/** Node types the expands experiment unlocks. */
export const REMIX_EXPAND_NODE_TYPES: readonly string[] = ['expand', 'nestedExpand'] as const;

const EXPAND_NODE_TYPES = new Set<string>(REMIX_EXPAND_NODE_TYPES);

/**
 * Deny-list check that additionally allows expands when `areExpandsEnabled` reports them enabled.
 *
 * The caller supplies the experiment read through its own product runtime, which keeps this
 * function pure and testable from both platform and product packages. It is passed as a thunk and
 * invoked only for expand node types: callers run this per selection change and per ADF node
 * during fragment filtering, so selecting a paragraph must never read the experiment (and so never
 * fires an exposure).
 */
export const isRemixRestrictedNodeTypeWithExpands = (
	nodeType: string,
	areExpandsEnabled: () => boolean,
): boolean => {
	if (EXPAND_NODE_TYPES.has(nodeType) && areExpandsEnabled()) {
		return false;
	}
	return isRemixRestrictedNodeType(nodeType);
};

/**
 * True if the given extension key is in the provided allow list.
 *
 * The allow list is passed in by the consumer (e.g. Confluence) so that
 * feature-flag checks and product-specific decisions stay outside platform
 * code.
 */
export const isAllowedMBEExtensionForRemix = (
	extensionKey: string | undefined,
	allowList: ReadonlyArray<string>,
): boolean => extensionKey !== undefined && allowList.includes(extensionKey);
